import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Laptop,
  Loader2,
  Lock,
  Monitor,
  Moon,
  RefreshCw,
  Smartphone,
  Sun,
  Tablet,
  Trash2,
  User as UserIcon,
  Phone,
  AlertCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader, StatusPill, Segmented, CopyId } from '@/components/page';
import { Notice, MarkTile, ago, fmtDate, plural } from '@/components/workspace-ui';
import { ConfirmDialog, SecretDialog, UserAvatar } from '@/components/admin-ui';
import { FieldError, OtpInput, PasswordInput, PasswordRules, passwordOk } from '@/components/auth-kit';
import { TotpQrCode } from '@/components/totp-qr-code';
import { useAuth, useToast, useTheme } from '@/hooks';
import { authApi, usersApi } from '@/api';
import { cn } from '@/lib/utils';
import type { MFAStatus, TOTPSetupData, Session } from '@/types';

const profileSchema = z.object({
  name: z.string().trim().min(2, 'Enter at least 2 characters.'),
  picture: z
    .string()
    .trim()
    .refine((v) => !v || /^https:\/\/[^\s]+\.[^\s]+/.test(v), 'Use an https:// image URL, or leave it empty.'),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: z
      .string()
      .min(1, 'Enter a new password.')
      .refine(passwordOk, 'Password does not meet every requirement below.'),
    confirmPassword: z.string().min(1, 'Repeat the new password.'),
  })
  .refine((d) => d.newPassword !== d.currentPassword, {
    message: 'Choose a password different from the current one.',
    path: ['newPassword'],
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords don’t match.',
    path: ['confirmPassword'],
  });

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

const NAV = [
  { id: 'profile', label: 'Profile', icon: UserIcon },
  { id: 'password', label: 'Password', icon: Lock },
  { id: 'mfa', label: 'Two-factor', icon: Phone },
  { id: 'sessions', label: 'Sessions', icon: Monitor },
  { id: 'appearance', label: 'Appearance', icon: Sun },
  { id: 'danger', label: 'Danger zone', icon: AlertCircle },
] as const;

const errMsg = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

export function SettingsPage() {
  const { user } = useAuth();
  const [active, setActive] = useState<string>(() => window.location.hash.slice(1) || 'profile');

  // Scroll-spy for the sub-navigation.
  useEffect(() => {
    const els = NAV.map((n) => document.getElementById(n.id)).filter(Boolean) as HTMLElement[];
    const io = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((x) => x.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (vis) setActive(vis.target.id);
      },
      { rootMargin: '-72px 0px -60% 0px' }
    );
    els.forEach((el) => io.observe(el));
    const h = window.location.hash.slice(1);
    if (h && document.getElementById(h)) {
      requestAnimationFrame(() => document.getElementById(h)?.scrollIntoView({ block: 'start' }));
    }
    return () => io.disconnect();
  }, []);

  const jump = (id: string) => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${id}`);
    setActive(id);
  };

  if (!user) return null;

  return (
    <div>
      <PageHeader
        title="Settings"
        description="Your profile, sign-in security and active sessions."
      />
      <div className="grid gap-6 lg:grid-cols-[200px_minmax(0,1fr)] lg:items-start">
        <nav
          aria-label="Settings sections"
          className="-mx-4 flex gap-1 overflow-x-auto px-4 pb-1 lg:sticky lg:top-20 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0"
        >
          {NAV.map(({ id, label, icon: Icon }) => (
            <a
              key={id}
              href={`#${id}`}
              aria-current={active === id ? 'true' : undefined}
              onClick={(e) => {
                e.preventDefault();
                jump(id);
              }}
              className={cn(
                'inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-2.5 text-[13.5px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground',
                active === id && 'bg-foreground/[0.06] text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </a>
          ))}
        </nav>

        <div className="min-w-0 space-y-6">
          <ProfileSection />
          <PasswordSection />
          <MfaSection email={user.email} isSuperAdmin={user.is_super_admin} />
          <SessionsSection />
          <AppearanceSection />
          <DangerSection email={user.email} />
        </div>
      </div>
    </div>
  );
}

function SectionCard({
  id,
  title,
  sub,
  action,
  danger,
  children,
}: {
  id: string;
  title: string;
  sub?: string;
  action?: React.ReactNode;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-20">
      <Card className={cn('overflow-hidden', danger && 'border-destructive/40')}>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3.5 sm:px-[18px]">
          <div className="min-w-0">
            <h2 id={`${id}-title`} className="text-[15px] font-semibold leading-tight">
              {title}
            </h2>
            {sub && <p className="mt-0.5 text-[13px] text-muted-foreground">{sub}</p>}
          </div>
          {action}
        </div>
        {children}
      </Card>
    </section>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  error,
  className,
  children,
}: {
  label: React.ReactNode;
  htmlFor: string;
  hint?: React.ReactNode;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? <FieldError id={`${htmlFor}-err`}>{error}</FieldError> : null}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/* ── Profile ─────────────────────────────────────────── */

function ProfileSection() {
  const { user, refetch } = useAuth();
  const { toast } = useToast();
  const [resending, setResending] = useState(false);

  const form = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: user?.name || '', picture: user?.picture || '' },
  });
  const { register, handleSubmit, reset, formState, watch } = form;
  const { errors, isDirty, isSubmitting } = formState;
  const liveName = watch('name');

  const onSubmit = async (data: ProfileForm) => {
    try {
      await authApi.updateProfile({ name: data.name, picture: data.picture || null });
      refetch();
      reset({ name: data.name, picture: data.picture });
      toast({ title: 'Profile updated' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not save', description: errMsg(e, 'Try again.') });
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await authApi.resendVerificationEmail();
      toast({ title: 'Verification email sent', description: user?.email });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not send email', description: errMsg(e, 'Try again.') });
    } finally {
      setResending(false);
    }
  };

  if (!user) return null;

  return (
    <SectionCard
      id="profile"
      title="Profile"
      sub="How you appear to admins, in member lists and in the audit log."
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="space-y-5 p-4 sm:p-[18px]">
          <div className="flex items-center gap-4">
            <UserAvatar
              name={liveName || user.name}
              email={user.email}
              picture={user.picture}
              className="h-14 w-14 text-base"
            />
            <div className="min-w-0">
              <b className="block truncate font-medium">{user.name || user.email}</b>
              <span className="block truncate font-mono text-xs text-muted-foreground">
                {user.email}
              </span>
              <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {user.is_super_admin && <StatusPill tone="neutral">Super admin</StatusPill>}
                <StatusPill tone={user.email_verified ? 'ok' : 'warn'}>
                  {user.email_verified ? 'Email verified' : 'Email unverified'}
                </StatusPill>
                {!user.email_verified && (
                  <Button type="button" variant="ghost" size="sm" onClick={resend} disabled={resending}>
                    {resending ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                    Resend link
                  </Button>
                )}
              </span>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name" htmlFor="pf-name" error={errors.name?.message}>
              <Input
                id="pf-name"
                autoComplete="name"
                aria-invalid={!!errors.name}
                {...register('name')}
              />
            </Field>
            <Field
              label="Email"
              htmlFor="pf-email"
              hint="Your sign-in email. A super admin can change it."
            >
              <Input id="pf-email" type="email" value={user.email} disabled className="font-mono" />
            </Field>
            <Field
              className="sm:col-span-2"
              label={
                <>
                  Profile picture URL <span className="font-normal text-muted-foreground">(optional)</span>
                </>
              }
              htmlFor="pf-picture"
              error={errors.picture?.message}
              hint="An HTTPS image. Leave empty to use your initials."
            >
              <Input
                id="pf-picture"
                type="url"
                inputMode="url"
                placeholder="https://…"
                className="font-mono"
                aria-invalid={!!errors.picture}
                {...register('picture')}
              />
            </Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t px-4 py-3 sm:px-[18px]">
          <Button type="button" variant="ghost" disabled={!isDirty} onClick={() => reset()}>
            Discard
          </Button>
          <Button type="submit" variant={isDirty ? 'default' : 'outline'} disabled={!isDirty || isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            Save changes
          </Button>
        </div>
      </form>
    </SectionCard>
  );
}

/* ── Password ────────────────────────────────────────── */

function PasswordSection() {
  const { toast } = useToast();
  const form = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const { register, handleSubmit, reset, formState, watch, setError } = form;
  const { errors, isSubmitting } = formState;
  const next = watch('newPassword');

  const onSubmit = async (data: PasswordForm) => {
    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      reset();
      toast({ title: 'Password updated', description: 'Use it the next time you sign in.' });
    } catch (e) {
      const message = errMsg(e, 'Failed to change password');
      if (/current password/i.test(message)) {
        setError('currentPassword', { message: 'Current password is incorrect.' });
      } else {
        toast({ variant: 'destructive', title: 'Could not change password', description: message });
      }
    }
  };

  return (
    <SectionCard id="password" title="Password" sub="Changing it keeps this session signed in.">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="space-y-4 p-4 sm:p-[18px]">
          <Field
            className="max-w-sm"
            label="Current password"
            htmlFor="pw-current"
            error={errors.currentPassword?.message}
          >
            <PasswordInput
              id="pw-current"
              className="h-9"
              autoComplete="current-password"
              aria-invalid={!!errors.currentPassword}
              {...register('currentPassword')}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="New password" htmlFor="pw-new" error={errors.newPassword?.message}>
              <PasswordInput
                id="pw-new"
                className="h-9"
                autoComplete="new-password"
                aria-describedby="pw-rules"
                aria-invalid={!!errors.newPassword}
                {...register('newPassword')}
              />
            </Field>
            <Field
              label="Confirm new password"
              htmlFor="pw-confirm"
              error={errors.confirmPassword?.message}
            >
              <PasswordInput
                id="pw-confirm"
                className="h-9"
                autoComplete="new-password"
                aria-invalid={!!errors.confirmPassword}
                {...register('confirmPassword')}
              />
            </Field>
            <div className="sm:col-span-2">
              <PasswordRules id="pw-rules" value={next} />
            </div>
          </div>
        </div>
        <div className="flex justify-end border-t px-4 py-3 sm:px-[18px]">
          <Button type="submit" variant="outline" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            Update password
          </Button>
        </div>
      </form>
    </SectionCard>
  );
}

/* ── Two-factor ──────────────────────────────────────── */

function MfaSection({ email, isSuperAdmin }: { email: string; isSuperAdmin: boolean }) {
  const { toast } = useToast();
  const [status, setStatus] = useState<MFAStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState<TOTPSetupData | null>(null);
  const [starting, setStarting] = useState(false);
  const [codes, setCodes] = useState<{ title: string; values: string[] } | null>(null);
  const [regen, setRegen] = useState(false);
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    authApi
      .getMFAStatus()
      .then(setStatus)
      .catch(() => setStatus({ enabled: false, method: null, configured_at: null }))
      .finally(() => setLoading(false));
  }, []);

  const start = async () => {
    setStarting(true);
    try {
      setSetup(await authApi.setupTOTP());
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not start setup', description: errMsg(e, 'Try again.') });
    } finally {
      setStarting(false);
    }
  };

  const on = !!status?.enabled;

  return (
    <SectionCard
      id="mfa"
      title="Two-factor authentication"
      sub="A 6-digit code from an authenticator app, asked at every sign-in."
      action={
        !loading && <StatusPill tone={on ? 'ok' : 'warn'}>{on ? 'On' : 'Off'}</StatusPill>
      }
    >
      <div className="space-y-4 p-4 sm:p-[18px]">
        {loading ? (
          <Skeleton className="h-16 w-full" />
        ) : on ? (
          <>
            <dl className="grid grid-cols-[130px_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-[13.5px]">
              <dt className="text-muted-foreground">Method</dt>
              <dd>Authenticator app (TOTP)</dd>
              <dt className="text-muted-foreground">Turned on</dt>
              <dd>{fmtDate(status?.configured_at)}</dd>
              <dt className="text-muted-foreground">Recovery codes</dt>
              <dd>Single-use codes. Regenerating replaces all of them.</dd>
            </dl>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setRegen(true)}>
                <RefreshCw />
                Regenerate recovery codes
              </Button>
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => setDisable(true)}
              >
                Turn off
              </Button>
            </div>
          </>
        ) : (
          <>
            <Notice tone="warn" title="Your account is protected by a password only">
              {isSuperAdmin
                ? 'Super admins can rotate signing keys and change every organization — turn this on.'
                : 'Anyone with your password can sign in to your apps.'}
            </Notice>
            <Button variant="outline" onClick={start} disabled={starting}>
              {starting ? <Loader2 className="animate-spin" /> : <Phone />}
              Set up authenticator app
            </Button>
          </>
        )}
      </div>

      {setup && (
        <MfaSetupDialog
          email={email}
          setup={setup}
          onClose={() => setSetup(null)}
          onEnabled={() => {
            setStatus({ enabled: true, method: 'totp', configured_at: new Date().toISOString() });
            setCodes({ title: 'Save your recovery codes', values: setup.backup_codes });
            setSetup(null);
          }}
        />
      )}
      {regen && (
        <CodeDialog
          title="Regenerate recovery codes?"
          body="Your current recovery codes stop working as soon as new ones are created."
          cta="Regenerate"
          danger
          onClose={() => setRegen(false)}
          onSubmit={async (code) => {
            const fresh = await authApi.regenerateBackupCodes({ code });
            setRegen(false);
            setCodes({ title: 'Your new recovery codes', values: fresh });
          }}
        />
      )}
      {disable && (
        <CodeDialog
          title="Turn off two-factor authentication?"
          body="Your account will be protected by your password only, and your recovery codes stop working."
          cta="Turn off"
          danger
          withPassword
          onClose={() => setDisable(false)}
          onSubmit={async (code, password) => {
            await authApi.disableMFA({ code, password: password ?? '' });
            setStatus({ enabled: false, method: null, configured_at: null });
            setDisable(false);
            toast({ title: 'Two-factor authentication is off' });
          }}
        />
      )}
      <SecretDialog
        open={!!codes}
        onClose={() => setCodes(null)}
        title={codes?.title ?? ''}
        description="Each code signs you in once if you lose your authenticator. They won’t be shown again."
        values={(codes?.values ?? []).map((v) => ({ value: v }))}
        downloadName="authrz-recovery-codes.txt"
        ackLabel="I’ve stored these codes somewhere safe"
      />
    </SectionCard>
  );
}

function MfaSetupDialog({
  email,
  setup,
  onClose,
  onEnabled,
}: {
  email: string;
  setup: TOTPSetupData;
  onClose: () => void;
  onEnabled: () => void;
}) {
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const verify = useCallback(
    async (c: string) => {
      setBusy(true);
      setErr('');
      try {
        await authApi.enableTOTP({ code: c });
        onEnabled();
      } catch (e) {
        setCode('');
        setErr(errMsg(e, 'That code didn’t match. Wait for a fresh code and try again.'));
      } finally {
        setBusy(false);
      }
    },
    [onEnabled]
  );

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Set up two-factor authentication</DialogTitle>
          <DialogDescription>
            Scan the code with an authenticator app such as 1Password, Google Authenticator or
            Authy, then enter the 6-digit code it shows.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5 sm:flex-row">
          <div className="flex shrink-0 flex-col items-center gap-2">
            <TotpQrCode value={setup.totp_uri} />
            <span className="text-xs text-muted-foreground">Scan with your app</span>
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <span className="text-[13px] font-medium">Can’t scan? Enter this key</span>
              <div className="mt-1.5">
                <CopyId value={setup.secret} />
              </div>
            </div>
            <dl className="grid grid-cols-[64px_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
              <dt className="text-muted-foreground">Account</dt>
              <dd className="truncate font-mono">{email}</dd>
              <dt className="text-muted-foreground">Issuer</dt>
              <dd>authrz.me</dd>
              <dt className="text-muted-foreground">Type</dt>
              <dd>Time-based · 6 digits · 30 s</dd>
            </dl>
          </div>
        </div>
        <div className="space-y-2">
          <span id="setup-otp-label" className="text-[13px] font-medium">
            Code from your app
          </span>
          <OtpInput
            value={code}
            onChange={(v) => {
              setCode(v);
              setErr('');
            }}
            onComplete={verify}
            invalid={!!err}
            labelledBy="setup-otp-label"
            disabled={busy}
            autoFocus
          />
          <FieldError>{err}</FieldError>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => verify(code)} disabled={code.length !== 6 || busy}>
            {busy && <Loader2 className="animate-spin" />}
            Verify and continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CodeDialog({
  title,
  body,
  cta,
  danger,
  withPassword,
  onClose,
  onSubmit,
}: {
  title: string;
  body: string;
  cta: string;
  danger?: boolean;
  withPassword?: boolean;
  onClose: () => void;
  onSubmit: (code: string, password?: string) => Promise<void>;
}) {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      await onSubmit(code, withPassword ? password : undefined);
    } catch (e) {
      setCode('');
      setErr(errMsg(e, 'That code didn’t match. Try the current one.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <span id="code-dlg-label" className="text-[13px] font-medium">
            Code from your authenticator app
          </span>
          <OtpInput
            value={code}
            onChange={(v) => {
              setCode(v);
              setErr('');
            }}
            invalid={!!err}
            labelledBy="code-dlg-label"
            disabled={busy}
            autoFocus
          />
          <FieldError>{err}</FieldError>
        </div>
        {withPassword && (
          <Field label="Password" htmlFor="code-dlg-pw">
            <PasswordInput
              id="code-dlg-pw"
              className="h-9"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'destructive' : 'default'}
            onClick={submit}
            disabled={busy || code.length !== 6 || (withPassword && !password)}
          >
            {busy && <Loader2 className="animate-spin" />}
            {cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Sessions ────────────────────────────────────────── */

function SessionsSection() {
  const { toast } = useToast();
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [revoke, setRevoke] = useState<Session | null>(null);
  const [revokeOthers, setRevokeOthers] = useState(false);

  const load = useCallback(() => {
    setLoadErr(null);
    authApi
      .getSessions()
      .then(setSessions)
      .catch((e) => setLoadErr(errMsg(e, 'Could not load sessions.')));
  }, []);
  useEffect(load, [load]);

  const list = (sessions ?? [])
    .slice()
    .sort(
      (a, b) =>
        Number(b.is_current) - Number(a.is_current) ||
        new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime()
    );
  const others = list.filter((s) => !s.is_current).length;
  const label = (s: Session) =>
    `${s.device_info?.browser ?? 'Unknown browser'} on ${s.device_info?.os ?? 'unknown OS'}`;

  return (
    <SectionCard
      id="sessions"
      title="Sessions"
      sub="Devices signed in to your account. Revoking one signs it out and stops its refresh tokens."
      action={
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          disabled={!others}
          onClick={() => setRevokeOthers(true)}
        >
          Sign out other sessions
        </Button>
      }
    >
      {loadErr ? (
        <div className="p-4">
          <Notice
            tone="bad"
            title="Couldn’t load sessions"
            action={
              <Button size="sm" variant="outline" onClick={load}>
                Retry
              </Button>
            }
          >
            {loadErr}
          </Notice>
        </div>
      ) : !sessions ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : (
        <ul className="divide-y">
          {list.map((s) => {
            const dev = s.device_info?.device?.toLowerCase() ?? '';
            const Icon = dev.includes('mobile') ? Smartphone : dev.includes('tablet') ? Tablet : Laptop;
            return (
              <li key={s.id} className="flex items-center gap-3 px-4 py-3 sm:px-[18px]">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-foreground/[0.04] text-muted-foreground">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <b className="truncate font-medium">{label(s)}</b>
                    {s.is_current && <StatusPill tone="ok">This device</StatusPill>}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    <span className="font-mono">{s.ip_address ?? '—'}</span>
                    {s.location ? ` · ${s.location}` : ''} ·{' '}
                    {s.is_current ? 'Active now' : `Active ${ago(s.last_activity_at)}`}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Signed in {fmtDate(s.created_at)}
                  </span>
                </div>
                {!s.is_current && (
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Revoke ${label(s)}`}
                    onClick={() => setRevoke(s)}
                  >
                    Revoke
                  </Button>
                )}
              </li>
            );
          })}
          {others === 0 && (
            <li className="px-4 py-3 text-[13px] text-muted-foreground sm:px-[18px]">
              No other active sessions.
            </li>
          )}
        </ul>
      )}

      {revoke && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setRevoke(null)}
          title="Revoke this session?"
          body={
            <>
              <b>{label(revoke)}</b> at <code className="font-mono">{revoke.ip_address ?? '—'}</code>
              {revoke.location ? ` (${revoke.location})` : ''} will be signed out immediately.
            </>
          }
          confirmLabel="Revoke session"
          danger
          onConfirm={async () => {
            try {
              await authApi.revokeSession(revoke.id);
              setSessions((cur) => (cur ?? []).filter((x) => x.id !== revoke.id));
              toast({ title: 'Session revoked', description: label(revoke) });
            } catch (e) {
              toast({ variant: 'destructive', title: 'Could not revoke', description: errMsg(e, 'Try again.') });
            }
          }}
        />
      )}
      <ConfirmDialog
        open={revokeOthers}
        onOpenChange={setRevokeOthers}
        title="Sign out other sessions?"
        body={`${plural(others, 'other session')} will be signed out. This device stays signed in.`}
        confirmLabel="Sign out others"
        danger
        onConfirm={async () => {
          try {
            await authApi.revokeAllOtherSessions();
            setSessions((cur) => (cur ?? []).filter((x) => x.is_current));
            toast({ title: 'Other sessions signed out', description: plural(others, 'session') + ' revoked' });
          } catch (e) {
            toast({ variant: 'destructive', title: 'Could not sign out', description: errMsg(e, 'Try again.') });
          }
        }}
      />
    </SectionCard>
  );
}

/* ── Appearance ──────────────────────────────────────── */

function AppearanceSection() {
  const { theme, setTheme } = useTheme();
  return (
    <SectionCard id="appearance" title="Appearance" sub="Applies to this browser only.">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-[18px]">
        <div>
          <b id="theme-label" className="text-[13.5px] font-semibold">
            Theme
          </b>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            Switch between the light and dark interface.
          </p>
        </div>
        <div className="flex items-center gap-2" aria-labelledby="theme-label">
          <Segmented<'light' | 'dark'>
            value={theme}
            onChange={setTheme}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
          {theme === 'dark' ? (
            <Moon className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Sun className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>
    </SectionCard>
  );
}

/* ── Danger zone ─────────────────────────────────────── */

function DangerSection({ email }: { email: string }) {
  const { toast } = useToast();
  const { data: owned = [] } = useQuery({
    queryKey: ['users', 'me', 'owned-organizations'],
    queryFn: usersApi.ownedOrganizations,
  });
  const [transfer, setTransfer] = useState(false);
  const [confirm, setConfirm] = useState(false);

  return (
    <SectionCard
      id="danger"
      title="Danger zone"
      sub="Irreversible. Read before you click."
      danger
    >
      <div className="space-y-4 p-4 sm:p-[18px]">
        {owned.length > 0 && (
          <Notice
            tone="warn"
            title={owned.length === 1 ? 'You own an organization' : `You own ${owned.length} organizations`}
          >
            Transfer ownership of{' '}
            {owned.map((o, i) => (
              <span key={o.id}>
                {i > 0 && ', '}
                <Link to={`/organizations/${o.id}`} className="font-medium text-foreground hover:underline">
                  {o.name}
                </Link>
              </span>
            ))}{' '}
            before deleting your account.
          </Notice>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <b className="text-[13.5px] font-semibold">Delete account</b>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              Removes your profile, sessions, app grants and organization memberships. Audit events
              you created are kept.
            </p>
          </div>
          <Button
            variant="outline"
            className="shrink-0 text-destructive hover:text-destructive"
            onClick={() => (owned.length ? setTransfer(true) : setConfirm(true))}
          >
            <Trash2 />
            Delete account…
          </Button>
        </div>
      </div>

      <Dialog open={transfer} onOpenChange={setTransfer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer ownership first</DialogTitle>
            <DialogDescription>
              You’re the owner of {owned.map((o) => o.name).join(', ')}. An organization can’t be
              left without an owner, so make another member the owner, then come back.
            </DialogDescription>
          </DialogHeader>
          <ul className="divide-y rounded-md border">
            {owned.map((o) => (
              <li key={o.id}>
                <Link
                  to={`/organizations/${o.id}`}
                  className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-foreground/[0.04]"
                  onClick={() => setTransfer(false)}
                >
                  <MarkTile name={o.name} size="sm" />
                  <span className="min-w-0 flex-1 truncate">{o.name}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransfer(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Delete your account?"
        body={
          <>
            This permanently removes <b className="font-mono">{email}</b>, signs out every session
            and revokes all app access. It can’t be undone.
          </>
        }
        confirmLabel="Delete account"
        danger
        typeToConfirm={email}
        onConfirm={async () => {
          try {
            await usersApi.deleteSelf(email);
            toast({ title: 'Account deleted', description: 'Signing you out…' });
            window.setTimeout(() => window.location.assign('/login'), 700);
          } catch (e) {
            toast({ variant: 'destructive', title: 'Could not delete account', description: errMsg(e, 'Try again.') });
          }
        }}
      />
    </SectionCard>
  );
}

